import type { Editor } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import {
  toSafeEditorErrorSummary,
  type SafeEditorErrorSummary,
} from './safe-editor-error';

/** Block separator used for every text offset in this module. */
const BLOCK_SEPARATOR = '\n';

const documentText = (doc: ProseMirrorNode): string =>
  doc.textBetween(0, doc.content.size, BLOCK_SEPARATOR);

/** How many characters of text precede `pos`. */
const textOffsetAt = (doc: ProseMirrorNode, pos: number): number =>
  doc.textBetween(0, pos, BLOCK_SEPARATOR).length;

/**
 * Inverse of {@link textOffsetAt}. Text offset is non-decreasing in position,
 * so a binary search inverts it without walking every node.
 */
const positionAtTextOffset = (doc: ProseMirrorNode, offset: number): number => {
  let low = 0;
  let high = doc.content.size;

  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (textOffsetAt(doc, mid) < offset) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }

  return low;
};

const commonPrefixLength = (a: string, b: string): number => {
  const max = Math.min(a.length, b.length);
  let i = 0;
  while (i < max && a[i] === b[i]) i++;
  return i;
};

const commonSuffixLength = (a: string, b: string, limit: number): number => {
  const max = Math.min(a.length, b.length, limit);
  let i = 0;
  while (i < max && a[a.length - 1 - i] === b[b.length - 1 - i]) i++;
  return i;
};

/**
 * Carry a text offset from the old document into the new one: before the first
 * difference it is unchanged, after the last difference it shifts by the length
 * delta, and inside the changed span it clamps — there is no honest answer for a
 * caret that sat in text which no longer exists.
 *
 * Exported for its boundary table; not part of the package's public surface.
 */
export const mapTextOffset = (offset: number, before: string, after: string): number => {
  // Every branch is clamped to the new document: the shift branch can otherwise
  // run past the end when the tail shares no common suffix.
  const inBounds = (value: number) => Math.min(Math.max(value, 0), after.length);

  const prefix = commonPrefixLength(before, after);
  if (offset <= prefix) {
    return inBounds(offset);
  }

  const suffix = commonSuffixLength(
    before,
    after,
    Math.min(before.length, after.length) - prefix
  );

  if (offset >= before.length - suffix) {
    return inBounds(offset + (after.length - before.length));
  }

  return inBounds(Math.min(Math.max(offset, prefix), after.length - suffix));
};

interface CaretSnapshot {
  text: string;
  fromOffset: number;
  toOffset: number;
}

/**
 * Text-offset view of the caret, taken before the document is replaced.
 *
 * Returns null rather than throwing, for any reason at all — a missing `doc`, a
 * node type that refuses `textBetween`. Applying the content is the caller's
 * job, and caret bookkeeping must never be able to prevent it.
 */
const readCaretSnapshot = (
  editor: Editor,
  from: number,
  to: number
): CaretSnapshot | null => {
  try {
    const doc = editor.state.doc;
    return {
      text: documentText(doc),
      fromOffset: textOffsetAt(doc, from),
      toOffset: textOffsetAt(doc, to),
    };
  } catch {
    return null;
  }
};

/**
 * Where the caret belongs in the replaced document. Keeps the raw positions
 * verbatim when there is no snapshot or the text is unchanged — several
 * positions share one text offset at node boundaries, so re-deriving one is not
 * guaranteed to return the position we started from.
 */
const remapCaret = (
  editor: Editor,
  snapshot: CaretSnapshot | null,
  from: number,
  to: number
): { from: number; to: number } => {
  if (!snapshot) {
    return { from, to };
  }

  const nextDoc = editor.state.doc;
  const nextText = documentText(nextDoc);
  if (nextText === snapshot.text) {
    return { from, to };
  }

  return {
    from: positionAtTextOffset(
      nextDoc,
      mapTextOffset(snapshot.fromOffset, snapshot.text, nextText)
    ),
    to: positionAtTextOffset(
      nextDoc,
      mapTextOffset(snapshot.toOffset, snapshot.text, nextText)
    ),
  };
};

/**
 * Runs `setContent` then restores the selection captured before the update.
 * Skips selection restore if the editor is destroyed after `setContent` (e.g.
 * unmount).
 *
 * The selection is carried by **text offset**, not absolute position: a note is
 * stored as markdown but edited as HTML, so a debounced save round-trips and
 * arrives back as fresh `content` that replaces the whole document, and a raw
 * position then addresses different text. See {@link mapTextOffset}.
 *
 * Both failure paths are reported to the host rather than logged here: the
 * package carries no application logging (`/core` bans it), and a raw editor
 * error can echo document content, so the selection-restore reporter receives
 * the typed content-free summary rather than the thrown value (task 4.10).
 * `setContent`'s own failure keeps handing the caller the raw error — that
 * caller is app code which applies its own envelope before logging.
 */
export function applyContentPreservingSelection(
  editor: Editor,
  setContent: () => void,
  onSetContentError: (error: unknown) => void,
  onSelectionRestoreError?: (summary: SafeEditorErrorSummary) => void
): void {
  if (editor.isDestroyed) {
    return;
  }

  const { $from, $to } = editor.state.selection;
  const snapshot = readCaretSnapshot(editor, $from.pos, $to.pos);

  try {
    setContent();
  } catch (error) {
    onSetContentError(error);
    return;
  }

  if (editor.isDestroyed) {
    return;
  }

  try {
    editor.commands.setTextSelection(remapCaret(editor, snapshot, $from.pos, $to.pos));
  } catch (error) {
    onSelectionRestoreError?.(toSafeEditorErrorSummary(error));
  }
}
